import type { FeatureKey, Page } from '@/types';
import type { Actor, AdminCustomer } from '@/types/admin';
import { buildSeed, DEMO_TENANT_ID, SEED_NOW, type TenantData } from '@/data/adminSeed';
import { ROLE_PERMISSIONS, isAdminRole, can, type Permission } from '@/lib/permissions';
import { ServiceError } from '../../contracts';
import { db, delay, shouldFail, uid } from '../db';

export { delay, uid, SEED_NOW, DEMO_TENANT_ID };
export type { TenantData };

/* --------------------------------------------------------------------------------------------
   MOCK tenant-partitioned store. `root[tenantId]` is a completely separate dataset; every service call
   resolves the tenant from the signed-in user, never from an argument. Persisted to localStorage so admin
   edits survive refresh (browser-local demo data, NOT a database).
-------------------------------------------------------------------------------------------- */
// v2 (Step 5): TenantData gained status/createdAt/ownerName/ownerEmail/planId/featureOverrides. Bumping the
// key forces a clean reseed instead of running with old records missing those fields.
const KEY = 'mce.mock.admin.v2';
let root: Record<string, TenantData> | undefined;

function load(): Record<string, TenantData> {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, TenantData>;
      if (parsed[DEMO_TENANT_ID]?.orders?.length && parsed[DEMO_TENANT_ID]?.planId) return parsed;
    }
  } catch { /* corrupt or unavailable: reseed */ }
  return buildSeed();
}

export const adminRoot = () => (root ??= load());

export function saveAdmin() {
  try { localStorage.setItem(KEY, JSON.stringify(adminRoot())); } catch { /* quota/unavailable: still works in memory */ }
}

export function resetAdmin() {
  root = buildSeed();
  saveAdmin();
}

export function dataFor(tenantId: string): TenantData {
  const t = adminRoot()[tenantId];
  if (!t) throw new ServiceError('Store not found', 'NOT_FOUND');
  return t;
}

/**
 * DEV ONLY: `?disable=wallet,loyalty`, `?enable=wallet`, `?resetDemo=1` on the demo tenant. The real,
 * supported way to do this is the Super Admin console's Tenant Feature Overrides screen (Step 5), which
 * goes through `platformServices.features.setOverride` -> `syncTenantFeatures`. This dev shortcut writes to
 * the SAME `featureOverrides` field (not a separate mechanism) so it stays consistent with that model and
 * survives a subsequent Super Admin sync instead of being silently overwritten.
 */
export function applyDevFlags() {
  if (typeof window === 'undefined') return;
  const sp = new URLSearchParams(window.location.search);
  if (sp.get('resetDemo') === '1') { resetAdmin(); db.reset(); }
  const d = dataFor(DEMO_TENANT_ID);
  let dirty = false;
  for (const [param, value] of [['disable', false], ['enable', true]] as const) {
    (sp.get(param) ?? '').split(',').filter(Boolean).forEach((k) => {
      if (k === 'all' && value) Object.keys(d.features).forEach((f) => { d.features[f as FeatureKey] = true; d.featureOverrides[f as FeatureKey] = true; });
      else if (k in d.features) { d.features[k as FeatureKey] = value; d.featureOverrides[k as FeatureKey] = value; }
      dirty = true;
    });
  }
  if (dirty) saveAdmin();
}
applyDevFlags();

/* --------------------------------------------------------------------------------------------
   Request context: who is calling, for which tenant, with which permission/feature requirement.
   This is the shape of the middleware chain the real API needs:
   authenticate -> resolveTenant -> requireRole -> requirePermission -> requireFeature.
-------------------------------------------------------------------------------------------- */
export function ctx(perm?: Permission, feature?: FeatureKey): { actor: Actor; data: TenantData } {
  const user = db.user;
  if (!user) throw new ServiceError('Please sign in to continue', 'UNAUTHORIZED');
  if (!isAdminRole(user.role)) throw new ServiceError('You do not have access to the merchant admin.', 'FORBIDDEN');
  const impersonating = user.role === 'SUPER_ADMIN';
  const tenantId = impersonating ? DEMO_TENANT_ID : user.tenantId;
  const data = dataFor(tenantId);
  // A platform admin suspends a store (Step 5) -> its own staff/admin can no longer use the merchant admin.
  // Support access (impersonation) is exempt, same as a platform admin being able to open a suspended tenant.
  if (!impersonating && data.status === 'SUSPENDED') {
    throw new ServiceError('Your store has been suspended by the platform administrator. Contact support to reactivate it.', 'FORBIDDEN');
  }
  // `?fail=1` simulates a backend outage for data endpoints (not the session check) so error states can be tested.
  if (perm && shouldFail()) throw new ServiceError('Simulated service failure (?fail is set).');
  if (perm && !can(user.role, perm)) throw new ServiceError(`Your role (${user.role.replace('_', ' ').toLowerCase()}) is not allowed to do this.`, 'FORBIDDEN');
  if (feature && !data.features[feature]) throw new ServiceError('This module is disabled for your store.', 'FEATURE_DISABLED');
  const actor: Actor = {
    user, tenantId, impersonating, permissions: ROLE_PERMISSIONS[user.role],
    tenant: { id: tenantId, name: data.store.name, slug: data.slug, features: { ...data.features }, logoUrl: data.branding.logoUrl },
  };
  return { actor, data };
}

/* ------------------------------ helpers ------------------------------ */
export function paginate<T>(items: T[], page = 1, pageSize = 10): Page<T> {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const p = Math.min(Math.max(1, page), totalPages);
  return { items: items.slice((p - 1) * pageSize, p * pageSize), total, page: p, pageSize, totalPages };
}

export const matches = (q: string | undefined, ...fields: (string | undefined)[]) => {
  const t = q?.trim().toLowerCase();
  return !t || fields.some((f) => f?.toLowerCase().includes(t));
};

export const nowIso = () => new Date().toISOString();
export const DAY_MS = 86_400_000;

/** "Today" for analytics: the newest order (or seed date), so the demo never appears empty. */
export const anchorTime = (d: TenantData) => Math.max(SEED_NOW, ...d.orders.slice(-5).map((o) => Date.parse(o.placedAt)));

export const REVENUE_STATUSES = ['PAID', 'PROCESSING', 'SHIPPED', 'DELIVERED'] as const;
export const isRevenue = (status: string) => (REVENUE_STATUSES as readonly string[]).includes(status);

/** Customer read model: money/points are DERIVED from the ledgers, never stored as editable numbers. */
export function hydrateCustomers(d: TenantData): AdminCustomer[] {
  const anchor = anchorTime(d);
  const wallet: Record<string, number> = {};
  const points: Record<string, number> = {};
  const last: Record<string, number> = {};
  const touch = (id: string, t: string) => { const v = Date.parse(t); if (v > (last[id] ?? 0)) last[id] = v; };
  d.wallet.forEach((w) => { wallet[w.customerId] = (wallet[w.customerId] ?? 0) + (w.direction === 'CREDIT' ? w.amount : -w.amount); touch(w.customerId, w.createdAt); });
  d.loyalty.forEach((l) => { points[l.customerId] = (points[l.customerId] ?? 0) + l.points; touch(l.customerId, l.createdAt); });
  const count: Record<string, number> = {};
  const spent: Record<string, number> = {};
  d.orders.forEach((o) => {
    touch(o.customer.id, o.placedAt);
    if (o.status !== 'CANCELLED') count[o.customer.id] = (count[o.customer.id] ?? 0) + 1;
    if (isRevenue(o.status)) spent[o.customer.id] = (spent[o.customer.id] ?? 0) + o.pricing.total;
  });
  return d.customers.map((c) => {
    const ordersCount = count[c.id] ?? 0;
    const totalSpent = spent[c.id] ?? 0;
    const tags: string[] = [];
    if (totalSpent >= 12000) tags.push('VIP');
    if (ordersCount >= 3) tags.push('Repeat');
    if (anchor - Date.parse(c.joinedAt) < 30 * DAY_MS) tags.push('New');
    if ((wallet[c.id] ?? 0) > 0) tags.push('Wallet');
    return {
      ...c, ordersCount, totalSpent, walletBalance: Math.max(0, wallet[c.id] ?? 0), loyaltyPoints: Math.max(0, points[c.id] ?? 0),
      lastActiveAt: new Date(Math.max(last[c.id] ?? 0, Date.parse(c.joinedAt))).toISOString(), tags,
    };
  });
}

/** Returns the cached result of a previous identical operation (idempotency), else null. */
export const seenKey = (d: TenantData, key: string) => d.idempotency[key] ?? null;
export const rememberKey = (d: TenantData, key: string, resultId: string) => { d.idempotency[key] = resultId; };

export const customerBalance = (d: TenantData, customerId: string) =>
  d.wallet.filter((w) => w.customerId === customerId).reduce((s, w) => s + (w.direction === 'CREDIT' ? w.amount : -w.amount), 0);

export const stockStatusOf = (stock: number, threshold: number) => (stock <= 0 ? 'OUT' : stock <= threshold ? 'LOW' : 'IN_STOCK') as 'OUT' | 'LOW' | 'IN_STOCK';
